import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errText, downloadFile } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { FileText } from "@phosphor-icons/react";

const Field = ({ label, value, onChange, testId, readOnly }) => (
  <div>
    <label className="text-xs font-semibold text-stone-500">{label}</label>
    <input value={value ?? ""} onChange={onChange} readOnly={readOnly} data-testid={testId}
      className={`mt-1 w-full min-h-[48px] px-3 rounded-lg border border-stone-300 ${readOnly ? "bg-stone-50 text-stone-500" : "bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"}`} />
  </div>
);

export default function MyProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    phone: user?.phone, address: user?.address,
    emergency_contact_name: user?.emergency_contact_name, emergency_contact_phone: user?.emergency_contact_phone,
    bank_name: user?.bank_name, bank_account: user?.bank_account, bank_ifsc: user?.bank_ifsc,
  });
  const [docs, setDocs] = useState([]);

  useEffect(() => { api.get("/documents/me").then((r) => setDocs(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    try {
      const r = await api.put("/profile", form);
      setUser(r.data);
      toast.success("Profile updated");
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-5" data-testid="my-profile-page">
      <h1 className="font-heading text-2xl font-extrabold">My profile</h1>

      <div className="bg-secondary text-white rounded-2xl p-6">
        <div className="font-heading text-xl font-extrabold">{user?.name}</div>
        <div className="text-sm text-stone-300 mt-1">{user?.designation} · {user?.department}</div>
        <div className="text-xs text-stone-400 mt-2 tabular-nums">{user?.employee_code} · Joined {user?.date_of_joining} · {user?.shift} shift</div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-4">
        <h3 className="font-heading font-bold text-sm">Contact</h3>
        <Field label="Email" value={user?.email} readOnly testId="profile-email" />
        <Field label="Phone" value={form.phone} onChange={set("phone")} testId="profile-phone" />
        <Field label="Address" value={form.address} onChange={set("address")} testId="profile-address" />
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-4">
        <h3 className="font-heading font-bold text-sm">Emergency contact</h3>
        <Field label="Name" value={form.emergency_contact_name} onChange={set("emergency_contact_name")} testId="profile-ec-name" />
        <Field label="Phone" value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")} testId="profile-ec-phone" />
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-4">
        <h3 className="font-heading font-bold text-sm">Bank details</h3>
        <Field label="Bank name" value={form.bank_name} onChange={set("bank_name")} testId="profile-bank-name" />
        <Field label="Account number" value={form.bank_account} onChange={set("bank_account")} testId="profile-bank-account" />
        <Field label="IFSC" value={form.bank_ifsc} onChange={set("bank_ifsc")} testId="profile-ifsc" />
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3">
        <h3 className="font-heading font-bold text-sm">My documents</h3>
        {docs.length === 0 ? <p className="text-sm text-stone-400">No documents shared by HR yet.</p> : docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-3" data-testid={`my-document-${d.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <FileText size={20} weight="duotone" className="text-primary shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{d.original_filename}</div>
                <div className="text-xs text-stone-500">{d.category}</div>
              </div>
            </div>
            <button onClick={() => downloadFile(`/documents/${d.id}/download`, d.original_filename).catch((e) => toast.error(errText(e)))}
              data-testid={`download-my-document-${d.id}`} className="text-primary font-bold text-xs shrink-0 hover:underline">Download</button>
          </div>
        ))}
      </div>

      <button onClick={save} data-testid="save-profile-button"
        className="tap-scale w-full min-h-[52px] rounded-full bg-primary text-white font-bold hover:bg-[#A63F07] transition-colors duration-200">Save profile</button>

      <button onClick={() => navigate("/change-password")} data-testid="profile-change-password-button"
        className="tap-scale w-full min-h-[48px] rounded-full bg-white border border-stone-300 text-stone-700 font-bold hover:bg-stone-50 transition-colors duration-200">Change password</button>
    </div>
  );
}
